import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdInsertComment, MdStar } from "react-icons/md";
import { isAuthenticated } from "../../auth";
import Vote, { ratingVote } from "./vote";
import { VoteStart } from "./voteStartCount";
import { elakayAutoText } from "../dataElakay";

const ViewVoteStarButton = ({
  infoSource,
  voteA,
  loadProductsView,
  productId,
  token,
  ButtonAddComments,
  ButtonBool = true,
  showComments,
  addComment,
  setAddComment,
  setVoteNumSt,
  VoteNumSt,
}) => {
  const navigate = useNavigate();
  const [hoveredStar, setHoveredStar] = useState(null);

  const voteRating = voteA && ratingVote(voteA);
  const votePercentages = {
    5: VoteStart(voteA, 5),
    4: VoteStart(voteA, 4),
    3: VoteStart(voteA, 3),
    2: VoteStart(voteA, 2),
    1: VoteStart(voteA, 1),
  };

  // Couleurs
  const colors = {
    primary: '#007399',
    primaryDark: '#004b63',
    primaryLight: '#00a4d8',
    background: '#f8fafc',
    white: '#ffffff',
    text: '#1e293b',
    textLight: '#64748b',
    border: '#e2e8f0',
    star: '#fbbf24',
  };

  // Styles épurés
  const styles = {
    container: {
      background: colors.white,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      padding: '2rem',
      marginBottom: '2rem',
    },
    summaryCard: {
      textAlign: 'center',
      padding: '1.5rem',
      background: colors.background,
      borderRadius: '8px',
    },
    ratingNumber: {
      fontSize: '3rem',
      fontWeight: '700',
      color: colors.primary,
      marginBottom: '0.5rem',
      lineHeight: '1',
    },
    starsWrapper: {
      fontSize: '1.5rem',
      marginBottom: '0.5rem',
    },
    userCount: {
      fontSize: '0.875rem',
      color: colors.textLight,
      fontWeight: '500',
    },
    progressCard: {
      padding: '1rem',
    },
    progressRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '1rem',
      gap: '1rem',
    },
    starLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      minWidth: '60px',
      fontSize: '0.9375rem',
      fontWeight: '500',
      color: colors.text,
    },
    starIcon: {
      color: colors.star,
      fontSize: '1.125rem',
    },
    progressBarWrapper: {
      flex: 1,
      height: '8px',
      backgroundColor: colors.border,
      borderRadius: '4px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'height 0.2s ease',
    },
    progressBarFill: {
      height: '100%',
      background: colors.primary,
      borderRadius: '4px',
      transition: 'width 0.3s ease',
      position: 'relative',
    },
    progressPercentage: {
      fontSize: '0.75rem',
      fontWeight: '500',
      color: colors.textLight,
      minWidth: '40px',
      textAlign: 'right',
    },
    buttonWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
    },
    actionButton: {
      width: '100%',
      padding: '0.75rem 1.5rem',
      fontSize: '0.9375rem',
      fontWeight: '500',
      color: colors.white,
      background: colors.primary,
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
  };

  // Handlers
  const handleStarClick = (percentage, starNumber) => {
    if (percentage > 0 && setVoteNumSt) {
      if (VoteNumSt === starNumber) {
        setVoteNumSt();
      } else {
        setVoteNumSt(starNumber);
      }
    }
  };

  const handleCommentButtonClick = () => {
    if (!showComments) {
      ButtonAddComments();
    } else if (!isAuthenticated()) {
      navigate("/signin");
    } else {
      setAddComment(!addComment);
    }
  };

  const getCommentButtonText = () => {
    if (!showComments) {
      return infoSource.language(infoSource, "title", "viewComment");
    }
    if (!isAuthenticated() || !addComment) {
      return infoSource.language(infoSource, "title", "addComment");
    }
    return infoSource.language(infoSource, "title", "viewAllComment");
  };

  // Composants
  const VoteProgressBar = ({ stars, percentage, isLast }) => (
    <div
      style={{
        ...styles.progressRow,
        marginBottom: isLast ? 0 : '1rem',
      }}
      onMouseEnter={() => setHoveredStar(stars)}
      onMouseLeave={() => setHoveredStar(null)}
    >
      <div style={styles.starLabel}>
        <span>{stars}</span>
        <MdStar style={styles.starIcon} />
      </div>
      <div
        style={{
          ...styles.progressBarWrapper,
          height: hoveredStar === stars ? '10px' : '8px',
        }}
        onClick={() => handleStarClick(percentage, stars)}
      >
        <div
          style={{
            ...styles.progressBarFill,
            width: `${percentage}%`,
          }}
        />
      </div>
      <div style={styles.progressPercentage}>
        {parseInt(percentage)}%
      </div>
    </div>
  );

  const VoteSummary = () => (
    <div style={styles.summaryCard}>
      <div style={styles.ratingNumber}>
        {parseFloat(voteRating || 0).toFixed(1)}
      </div>
      <div style={styles.starsWrapper}>
        <Vote
          loadProductsView={loadProductsView}
          productID={productId}
          token={token}
          rating={voteRating || 0}
        />
      </div>
      <p style={styles.userCount}>
        {voteA?.length || 0} {voteA?.length === 1 ? elakayAutoText("h_auto_user_singular_lower", "utilisateur") : elakayAutoText("h_auto_user_plural_lower", "utilisateurs")}
      </p>
    </div>
  );

  return (
    <div style={styles.container}>
      <div className="row">
        {/* Vote Summary */}
        <div className="col col-12 col-md-4 mb-3 mb-md-0">
          <VoteSummary />
        </div>

        {/* Progress Bars */}
        <div className="col col-12 col-md-4 mb-3 mb-md-0">
          <div style={styles.progressCard}>
            {Object.entries(votePercentages)
              .reverse()
              .map(([stars, percentage], index, array) => (
                <VoteProgressBar
                  key={stars}
                  stars={parseInt(stars)}
                  percentage={percentage}
                  isLast={index === array.length - 1}
                />
              ))}
          </div>
        </div>

        {/* Action Button */}
        {ButtonBool && (
          <div className="col col-12 col-md-4">
            <div style={styles.buttonWrapper}>
              <button
                type="button"
                style={styles.actionButton}
                onClick={handleCommentButtonClick}
                onMouseEnter={(e) => {
                  e.target.style.background = colors.primaryDark;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = colors.primary;
                }}
              >
                <MdInsertComment size={20} />
                <span>{getCommentButtonText()}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewVoteStarButton;
